import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { POLL_INTERVAL_MS, isGenerating, type QrBatchList } from '../lib/qrBatch';
import { qrBatchListKey, qrBatchStatsKey } from '../lib/queryKeys';

export interface UseQrBatches {
  list: QrBatchList | undefined;
  isPending: boolean;
  isError: boolean;
  // A batch is being generated: the form waits and the list keeps polling.
  isGenerating: boolean;
  retry: () => void;
}

// The list polls ONLY while a batch is generating — every minute, the pace the generator works at —
// and stops by itself when the last one settles. Errors stay inline (default `throwOnError: false`):
// a failed list surfaces a retry on its own card.
export function useQrBatches(fetchQrBatches: () => Promise<QrBatchList>): UseQrBatches {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: qrBatchListKey(),
    queryFn: fetchQrBatches,
    refetchInterval: (current) => (isGenerating(current.state.data) ? POLL_INTERVAL_MS : false),
  });

  const generating = isGenerating(query.data);
  const wasGenerating = useRef(generating);

  // The moment a batch settles, the stats it changed are read once more — their own polling has
  // just stopped with it, one beat too early to have seen the result.
  useEffect(() => {
    if (wasGenerating.current && !generating) {
      void queryClient.invalidateQueries({ queryKey: qrBatchStatsKey() });
    }
    wasGenerating.current = generating;
  }, [generating, queryClient]);

  return {
    list: query.data,
    isPending: query.isPending,
    isError: query.isError,
    isGenerating: generating,
    retry: () => {
      void query.refetch();
    },
  };
}
